export const ACCOUNT_PAGE_STATUSES = [
  'published',
  'archived',
  'unpublished',
] as const;

export type AccountPageStatus = (typeof ACCOUNT_PAGE_STATUSES)[number];

export const REQUIRED_ACCOUNT_CSV_COLUMNS = [
  'accountName',
  'accountSlug',
] as const;

export const ACCOUNT_CSV_METADATA_COLUMNS = [
  ...REQUIRED_ACCOUNT_CSV_COLUMNS,
] as const;

export type AccountCsvRow = Record<string, string>;

const VARIABLE_PATTERN = /\[\[\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\]\]/g;

export function normalizeAccountSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function extractVariablesFromString(value: string): string[] {
  const variables = new Set<string>();

  for (const match of value.matchAll(VARIABLE_PATTERN)) {
    variables.add(match[1]);
  }

  return Array.from(variables).sort();
}

export function extractVariablesFromValue(value: unknown): string[] {
  const variables = new Set<string>();

  function visit(item: unknown) {
    if (typeof item === 'string') {
      extractVariablesFromString(item).forEach((variable) =>
        variables.add(variable),
      );
      return;
    }

    if (Array.isArray(item)) {
      item.forEach(visit);
      return;
    }

    if (item && typeof item === 'object') {
      Object.values(item).forEach(visit);
    }
  }

  visit(value);

  return Array.from(variables).sort();
}

export function replaceTemplateVariables<T>(
  value: T,
  variables: AccountCsvRow,
): T {
  function replace(item: unknown): unknown {
    if (typeof item === 'string') {
      return item.replace(VARIABLE_PATTERN, (match, variable: string) => {
        const replacement = variables[variable];
        return replacement === undefined || replacement === ''
          ? match
          : replacement;
      });
    }

    if (Array.isArray(item)) {
      return item.map(replace);
    }

    if (item && typeof item === 'object') {
      return Object.fromEntries(
        Object.entries(item).map(([key, nestedValue]) => [
          key,
          replace(nestedValue),
        ]),
      );
    }

    return item;
  }

  return replace(value) as T;
}

export function serializeCsv(rows: string[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          if (/[",\n\r]/.test(cell)) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        })
        .join(','),
    )
    .join('\n');
}

export function parseCsv(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < csv.length; index += 1) {
    const char = csv[index];
    const nextChar = csv[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(cell.trim());
      cell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      row.push(cell.trim());
      if (row.some(Boolean)) {
        rows.push(row);
      }
      row = [];
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell.trim());
  if (row.some(Boolean)) {
    rows.push(row);
  }

  return rows;
}

export function createAccountCsvTemplate(variables: string[]): string {
  const headers = [
    ...ACCOUNT_CSV_METADATA_COLUMNS,
    ...variables.filter(
      (variable) =>
        !ACCOUNT_CSV_METADATA_COLUMNS.includes(
          variable as (typeof ACCOUNT_CSV_METADATA_COLUMNS)[number],
        ),
    ),
  ];

  return serializeCsv([
    headers,
    [
      'Example Company',
      'example-company-campaign',
      ...headers
        .slice(ACCOUNT_CSV_METADATA_COLUMNS.length)
        .map((header) => `Personalized value for ${header}`),
    ],
  ]);
}

export function rowsFromCsv(csv: string): AccountCsvRow[] {
  const [headers, ...rows] = parseCsv(csv);
  if (!headers?.length) {
    return [];
  }

  return rows.map((row) =>
    Object.fromEntries(
      headers.map((header, index) => [header, row[index] ?? '']),
    ),
  );
}

export function validateAccountCsvHeaders({
  headers,
  variables,
}: {
  headers: string[];
  variables: string[];
}) {
  const allowedHeaders = new Set([
    ...ACCOUNT_CSV_METADATA_COLUMNS,
    ...variables,
  ]);
  const requiredHeaders = new Set(REQUIRED_ACCOUNT_CSV_COLUMNS);
  const missingRequiredHeaders = Array.from(requiredHeaders).filter(
    (header) => !headers.includes(header),
  );
  const unknownHeaders = headers.filter(
    (header) => !allowedHeaders.has(header),
  );
  const missingVariableHeaders = variables.filter(
    (variable) => !headers.includes(variable),
  );

  return {
    isValid:
      missingRequiredHeaders.length === 0 &&
      unknownHeaders.length === 0 &&
      missingVariableHeaders.length === 0,
    missingRequiredHeaders,
    missingVariableHeaders,
    unknownHeaders,
  };
}

export function getRawPersonalizationValues({
  row,
  variables,
}: {
  row: AccountCsvRow;
  variables: string[];
}) {
  return Object.fromEntries(
    variables.map((variable) => [variable, row[variable] ?? '']),
  );
}
